
import React, { useState, useEffect, useContext } from 'react';
import { getProofIndices } from '../../web3/merkleProof';
import { Button, Container, Row, Col } from "react-bootstrap";
import { useAccount, useSwitchChain } from 'wagmi';
import { fromWei } from 'web3-utils';
import { useWeb3js, useWeb3jsSigner } from '../../web3/web3Client';
import { getContract, getContractAddress } from '../../web3/contractBuilder';
import { genesisChainId } from './constants';
import { useConfirm, usePending } from '../../hooks';
import { StakingContext } from '../../contexts/StakingContext';
import { GenesisSelection } from './components/GenesisWidget';
import { three_decimals } from '../../helpers';
import { SwitchNetwork, SuccessModal } from './Modals';
import { getRewardDetails, getPayoutClaimSignature, updateClaimStatus, getProofByIndex } from '../../services/reward';

import './Claim.scss';


export const Status = {
    INITIAL: 'initial',
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
}

export function Claim(props) {
    const {address, status, chainId} = useAccount();
    const { chains, switchChain } = useSwitchChain();
    const web3js = useWeb3jsSigner(genesisChainId);
    const web3Reader = useWeb3js({ chainId: genesisChainId});
    const [claimableRemarks, setClaimableRemarks] = useState([]);
    const [claimableAmount, setClaimableAmount] = useState(null);
    const [fetchingStatus, setFetchingStatus] = useState(Status.INITIAL);
    const [proof, setProof] = useState(null);
    const [pending, setPending] = useState(false);
    const [paused, setPaused] = useState(false);
    const [isClaimed, setIsClaimed] = useState(false);

    useEffect(() => {
        const init = async () => {
            setFetchingStatus(Status.LOADING);
            try {
                const contractAddress = getContractAddress("PayoutClaim");
                const payoutContract = getContract("PayoutClaim", web3Reader);

                const isPaused = await payoutContract.methods.paused().call();
                setPaused(isPaused);

                const reward = await getRewardDetails(contractAddress, address.toLowerCase());
                if (reward == null) {
                    setIsClaimed(true);
                } else {
                    // setRewardDetails(reward);
                    const claimed = await payoutContract.methods.claimed(reward.leaf_hash).call();
                    if (claimed) {
                        return updateClaimStatus(contractAddress, address);
                    } else {
                        setClaimableAmount(reward?.rewards[0])
                        setClaimableRemarks(reward?.remarks)

                        const merkleRoot = await payoutContract.methods.root().call();
                        const proofIndices = getProofIndices(reward.leaf_index, reward.leaf_count);
                        setProof(await Promise.all(proofIndices.map(({index, layer}) => getProofByIndex(merkleRoot, index, layer))));
                    }
                }
                setFetchingStatus(Status.SUCCESS);
            } catch (e) {
                console.error(e);
                // setRewardDetails(null);
                setFetchingStatus(Status.ERROR)
            }
        }

        if (status == "connected" && address) {
            init();
        }
    }, [status, address])

    const claimReward = async () => {
        try {
            if(!claimableAmount) return;

            setPending(true);
            if(chainId !== genesisChainId) {   
                let prompt = await usePending(SwitchNetwork, () => switchChain({ chainId: genesisChainId }))
                if(!prompt) {
                    setPending(false)
                    return;
                }
            }

            const contractAddress = getContractAddress("PayoutClaim");
            const payoutContract = getContract("PayoutClaim", web3js);
            const { signature, expiry } = await getPayoutClaimSignature(contractAddress, address);
            console.log(address, claimableAmount, proof, signature, expiry)
            const result = await payoutContract.methods.claimPayout(address, claimableAmount, proof, signature, expiry)
                .send({ 
                    from: address,
                    maxPriorityFeePerGas: null, // needed for BNB
                    maxFeePerGas: null          // needed for BNB
                });

            // const check = await usePending(PendingStake, tx, { data: selectedTokenIds.length, title: "Unstake Your pNFTs", btn: "Unstake PNFTs" })
            await updateClaimStatus(CLAIM_CONTRACT_ADDRESS, address);
            
            setIsClaimed(true);
            useConfirm(SuccessModal, { title: "CONGRATULATIONS!", message: "You have successfully claimed your rewards." })
            return result;
        } catch (e) {
            console.error(e);
        } finally {
            setPending(false)
        }
    }

    // if(isClaimed) return <div id="claim">
    //     <Container>
    //         You have already claimed all your rewards at this time.
    //     </Container>
    // </div>

    return <div id="claim">
        <Container>
            <section>

            <h1>CLAIM YOUR REWARDS</h1>
            <p>Claim your $EDU token rewards easily and have them transferred directly back to your own wallet.</p>

            <DataPanel data={claimableRemarks} />

            {status == "connected" && address && claimableAmount
                ? <div className='staking-action-panel'>
                    {paused ? 'Claiming is temporarily paused. Please check back later.' : ''}
                    <div className='ctrls'>
                        <div>
                        <p style={{fontWeight: '600'}}>Claimable Rewards</p>
                        <h3>{!claimableAmount ? '-' : three_decimals.format(fromWei(claimableAmount, 'ether'))} $EDU</h3>
                        </div>
                        <Button onClick={claimReward} disabled={fetchingStatus === Status.LOADING || fetchingStatus === Status.ERROR || pending || paused}>Claim Rewards</Button>
                    </div>
                </div>
                : <></>
            }
            </section>
        </Container>
    </div>
}


function DataPanel(props) {
    const { data } = props;

    return <Container className='oc-data-table'>
        <Row className='table-header'>
            <Col lg={5}>Item</Col>
            <Col lg={3}>Genesis NFT Paired</Col>
            <Col lg={4}>Claimable Reward</Col>
        </Row>
        {data?.length > 0 && data
            .map(x => { return {...x, id: x.publisherNFTId}}) // potentially sort by stake timestamps
            .map((x, i) => (<PNFT key={i} {...x} />))}
    </Container>
    
}

function PNFT({ id, genesis1Quantity, genesis2Quantity, amount }) {
    const { metadata } = useContext(StakingContext)
    const [name, setName] = useState()
    const [nftId, setNftId] = useState()
    const [imgSrc, setImgSrc] = useState()
    const [rarity, setRarity] = useState()
    
    useEffect(() => {
        id = id.toString()
        if(metadata && metadata[id]) {
            const data = metadata[id]
            setName(data?.name)
            setNftId(data?.id)
            setImgSrc(data?.image)
            setRarity(data?.tier.toLowerCase())
        }
    }, [metadata, id])
    
    return <Row>
        <Col lg={5}>
            <div className='pnft-item'>
                <div className='img-wrapper'>
                    <img src={imgSrc} width={60} />
                </div>
                <div>
                    <p className='emph'>{name} #{nftId}</p>
                    <p>{rarity}</p>
                </div>
            </div>
        </Col>
        <Col lg={3}>
            <GenesisSelection 
                data={{silver: genesis2Quantity, golden: genesis1Quantity}}
                readOnly
            />
        </Col> {/** enabled by checkbox */}
        <Col lg={4}>{three_decimals.format(fromWei(amount, 'ether'))} EDU</Col>
    </Row>
}